import React from 'react';
import { createPortal } from 'react-dom';

import type { ConfirmEventResult } from '../../api/events';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEventConfirmationState from '../../hooks/useEventConfirmationState';
import EventConfirmationEvidence from './EventConfirmationEvidence';
import './EventConfirmation.css';

type EventConfirmationModalProps = {
  open: boolean;
  eventId: string;
  eventStatus?: string | null;
  onClose: () => void;
  onCompleted?: (result: ConfirmEventResult) => void | Promise<void>;
  onSendToClient?: () => void;
};

const FIRM_EVENT_STATUSES = new Set([
  'confirmed',
  'picking',
  'partially_filled',
  'filled',
  'partially_shipped',
  'shipped',
  'on_rent',
  'partially_returned',
  'returning',
  'returned',
  'ready_to_invoice',
  'invoiced',
  'closed',
]);

export default function EventConfirmationModal({
  open,
  eventId,
  eventStatus,
  onClose,
  onCompleted,
  onSendToClient,
}: EventConfirmationModalProps) {
  const confirmation = useEventConfirmationState(eventId, { enabled: open });
  const [reason, setReason] = React.useState('');
  const [acknowledged, setAcknowledged] = React.useState(false);
  const dialogRef = React.useRef<HTMLFormElement>(null);
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  useBodyScrollLock(open);

  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    return () => {
      if (previouslyFocused?.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    setReason('');
    setAcknowledged(false);
    const timer = window.setTimeout(() => titleRef.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(timer);
  }, [eventId, open]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Escape' && !confirmation.submitting) {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ) ?? []);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (
      document.activeElement === first
      || document.activeElement === titleRef.current
    )) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, [confirmation.submitting, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const state = confirmation.state;
  const effectiveStatus = String(state?.eventStatus ?? eventStatus ?? '').trim().toLowerCase();
  const alreadyFirm = FIRM_EVENT_STATUSES.has(effectiveStatus);
  const pending = Boolean(state?.pendingRequest);
  const signatureRequired = (state?.policy ?? 'client_signature_required') === 'client_signature_required';
  const managerRequired = state?.requiresManagerApproval
    ?? state?.policy === 'manager_bypass_required';
  const canSubmit = Boolean(
    state
    && state.canRequest
    && !pending
    && !alreadyFirm
    && !signatureRequired
    && reason.trim()
    && acknowledged
    && !confirmation.submitting,
  );
  const actionLabel = managerRequired
    ? 'Request manager approval'
    : 'Confirm without client signature';
  const modalTitle = pending
    ? 'Confirmation approval pending'
    : alreadyFirm
      ? 'Confirmation evidence'
      : signatureRequired
        ? 'Client signature required'
        : managerRequired
          ? 'Request confirmation without a signature'
          : 'Confirm without a client signature';

  return createPortal(
    <div
      className="modal-overlay modal-overlay--solid event-confirmation-modal__overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !confirmation.submitting) onClose();
      }}
    >
      <form
        ref={dialogRef}
        className="modal modal--md event-confirmation-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-confirmation-modal-title"
        onKeyDown={handleKeyDown}
        onSubmit={async (event) => {
          event.preventDefault();
          if (!canSubmit) return;
          try {
            const result = await confirmation.confirmWithoutSignature(reason.trim());
            await onCompleted?.(result);
          } catch {
            // The hook keeps the API error visible in the dialog.
          }
        }}
      >
        <div className="modal__header">
          <div>
            <p className="event-confirmation-modal__kicker">Inventory commitment</p>
            <h2
              ref={titleRef}
              id="event-confirmation-modal-title"
              className="modal__title"
              tabIndex={-1}
            >
              {modalTitle}
            </h2>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            aria-label="Close"
            disabled={confirmation.submitting}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div className="modal__body event-confirmation-modal__body">
          <EventConfirmationEvidence
            confirmation={state}
            eventStatus={eventStatus}
            loading={confirmation.loading}
            error={confirmation.error}
          />

          {!confirmation.loading && confirmation.error ? (
            <div className="alert alert-error" role="alert">
              <span>{confirmation.error}</span>
              <button
                type="button"
                className="btn xs subtle"
                onClick={() => void confirmation.refresh()}
                disabled={confirmation.refreshing}
              >
                {confirmation.refreshing ? 'Retrying...' : 'Retry'}
              </button>
            </div>
          ) : null}

          {!confirmation.loading && state && !pending && !alreadyFirm && !signatureRequired ? (
            <>
              <label className="event-confirmation-modal__reason">
                <span>{managerRequired ? 'Reason for requesting the exception' : 'Reason for confirming without a signature'}</span>
                <textarea
                  className="control"
                  rows={4}
                  maxLength={2000}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Record the business reason and any client communication the team should retain."
                  disabled={confirmation.submitting}
                  required
                />
                <small className="fg-muted">Required - {reason.length}/2000</small>
              </label>
              <label className="event-confirmation-modal__acknowledgement">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  disabled={confirmation.submitting}
                />
                <span>
                  I understand that no client signature is attached. This action and reason will be retained with the event.
                </span>
              </label>
              {!state.canRequest ? (
                <div className="alert alert-warning" role="status">
                  You do not have permission to request this confirmation exception.
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="modal__footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={confirmation.submitting}
          >
            {pending || alreadyFirm ? 'Done' : 'Keep current stage'}
          </button>
          {!pending && !alreadyFirm && signatureRequired && onSendToClient ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSendToClient}
              disabled={confirmation.loading}
            >
              Send to client
            </button>
          ) : null}
          {!pending && !alreadyFirm && !signatureRequired ? (
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {confirmation.submitting
                ? managerRequired
                  ? 'Requesting...'
                  : 'Confirming...'
                : actionLabel}
            </button>
          ) : null}
        </div>
      </form>
    </div>,
    document.body,
  );
}
